import {DialogFooter, DialogDismiss, DialogHeading, DialogProvider} from '@/components/ui/dialog-atoms/DialogAtoms'
import {
    OpsVettingCheckIconContainer,
    OpsVettingChoosePhotoDialog,
    OpsVettingChoosePhotoDialogBody
} from '@/features/ops-vetting/components/ops-vetting-atoms/OpsVettingAtoms'
import {FC, useState} from 'react'
import {useGetExperienceVettingGalleryImages} from '../../queries/useGetExperienceVettingGalleryImages'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {AlertTriangleIcon, CheckIcon} from '@/components/ui/icon/Icon'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {StyledGalleryItemSkeleton} from '@/components/commons/gallery-image-item/style'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {Button} from '@/components/ui/button/Button'
import {useTranslation} from '@/translations/i18n'
import {DialogStore} from '@ariakit/react'
import {StyledDialogHeader, StyledDialogHeaderTitle, StyledGalleryImageItem} from './style'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Chip} from '@/components/commons/chip/Chip'
import {useUpdateExperienceVettingCriteriaItineraryCoveryImage} from '../../queries/useUpdateExperienceVettingCriteriaItineraryCoveryImage'

interface ExpProPhotosChangeItineraryPhotoModalProps {
    itineraryId: number
    itineraryTitle: string
    itineraryCoverImageId?: number
    experienceId: number
    galleryId: number
    store: DialogStore
    onClose: () => void
}

export const ExpProPhotosChangeItineraryPhotoModal: FC<ExpProPhotosChangeItineraryPhotoModalProps> = ({
    itineraryId,
    itineraryTitle,
    itineraryCoverImageId,
    experienceId,
    galleryId,
    store,
    onClose
}) => {
    const [selectedImageId, setSelectedImageId] = useState<number | undefined>(itineraryCoverImageId)
    const {data: images, isPending, isError} = useGetExperienceVettingGalleryImages({experienceId, galleryId})
    const {t} = useTranslation()
    const updatePhotoMutation = useUpdateExperienceVettingCriteriaItineraryCoveryImage(
        {
            experienceId,
            itineraryId
        },
        {
            onSuccess: onClose
        }
    )

    const onConfirm = (id: number) => {
        updatePhotoMutation.mutate({coverImageId: id})
    }

    return (
        <DialogProvider
            store={store}
            setOpen={() => {
                setSelectedImageId(itineraryCoverImageId)
            }}
        >
            <OpsVettingChoosePhotoDialog width="680px">
                <StyledDialogHeader style={{position: 'relative'}}>
                    <DialogDismiss />
                    <StyledDialogHeaderTitle direction="column" align="center" justify="center">
                        <DialogHeading>{t('commons:choose_a_photo')}</DialogHeading>
                        <span>{itineraryTitle}</span>
                    </StyledDialogHeaderTitle>
                </StyledDialogHeader>
                <ScrollArea scrollbar={<Scrollbar />}>
                    <OpsVettingChoosePhotoDialogBody>
                        {isPending ? (
                            Array.from({length: 6}).map((_, index) => <StyledGalleryItemSkeleton key={index} />)
                        ) : isError ? (
                            <ErrorBox icon={<AlertTriangleIcon />} title={t('errors:default')} />
                        ) : (
                            images?.map(image => (
                                <StyledGalleryImageItem
                                    isSelected={selectedImageId == image.id}
                                    key={image.id}
                                    onClick={() => setSelectedImageId(image.id)}
                                    topLeftSlot={
                                        selectedImageId == image.id && (
                                            <OpsVettingCheckIconContainer>
                                                <CheckIcon />
                                            </OpsVettingCheckIconContainer>
                                        )
                                    }
                                    bottomLeftSlot={
                                        image.isProPhotography ? (
                                            <Chip
                                                label={t('vetting:reordering:pro_photo')}
                                                variant={'neutral'}
                                                shape="roundedRectangle"
                                            />
                                        ) : (
                                            image.isHostApprovalNeeded == false && (
                                                <Chip
                                                    label={t('vetting:reordering:host_provided')}
                                                    variant={'neutral'}
                                                    shape="roundedRectangle"
                                                />
                                            )
                                        )
                                    }
                                    src={image.url}
                                />
                            ))
                        )}
                    </OpsVettingChoosePhotoDialogBody>
                </ScrollArea>
                <DialogFooter>
                    <DialogDismiss render={<Button variant="tertiary" />} onClick={onClose}>
                        {t('commons:cancel')}
                    </DialogDismiss>

                    <Button
                        disabled={!selectedImageId || updatePhotoMutation.isPending}
                        onClick={() => selectedImageId && onConfirm(selectedImageId)}
                    >
                        {t('commons:confirm')}
                        {updatePhotoMutation.isPending && <Spinner />}
                    </Button>
                </DialogFooter>
            </OpsVettingChoosePhotoDialog>
        </DialogProvider>
    )
}
